import { useCallback, useMemo, useRef, useState } from 'react';
import type { Canceler } from 'axios';
import axios from 'axios';
import { debounce } from 'lodash';

import { useCms } from '~/components/hooks/useCms';
import { reportAxiosError } from '~/Utils';

import type {
  GbgAddressFindResponse,
  GbgAddressRetrieveResponse,
  GbgAddressSuggestion,
  GbgRetrievedAddress,
} from './types';

interface UseGbgAddressAutocompleteOptions {
  country: string;
  maxResults?: number;
  debounceMs?: number;
}

interface UseGbgAddressAutocompleteReturn {
  suggestions: GbgAddressSuggestion[];
  isLoading: boolean;
  retrievedAddress: GbgRetrievedAddress | null;
  search: (input: string) => void;
  selectSuggestion: (suggestionId: string) => Promise<GbgRetrievedAddress | null>;
  clearSuggestions: () => void;
}

export const useGbgAddressAutocomplete = ({
  country,
  maxResults = 10,
  debounceMs = 300,
}: UseGbgAddressAutocompleteOptions): UseGbgAddressAutocompleteReturn => {
  const { userOrganization } = useCms();
  const [suggestions, setSuggestions] = useState<GbgAddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [retrievedAddress, setRetrievedAddress] = useState<GbgRetrievedAddress | null>(null);
  const cancelRef = useRef<Canceler | null>(null);

  const baseRoute = `/api/v1/organizations/${userOrganization.id}/gbg/address`;

  const fetchSuggestions = useCallback(
    async (input: string) => {
      if (!input.trim()) {
        setSuggestions([]);
        return;
      }

      cancelRef.current?.();
      let cancelled = false;

      try {
        setIsLoading(true);
        const { data } = await axios.get<GbgAddressFindResponse>(`${baseRoute}/find`, {
          params: { full_address: input, country, max_results: maxResults },
          cancelToken: new axios.CancelToken((c) => {
            cancelRef.current = c;
          }),
        });
        setSuggestions(data.suggestions);
      } catch (error) {
        cancelled = axios.isCancel(error);
        if (!cancelled) {
          setSuggestions([]);
          // Don't open a dialog for this error, so the user can continue typing manually
          reportAxiosError(error, false);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    },
    [baseRoute, country, maxResults]
  );

  const debouncedSearch = useMemo(
    () => debounce((input: string) => fetchSuggestions(input), debounceMs),
    [fetchSuggestions, debounceMs]
  );

  const search = useCallback(
    (input: string) => {
      if (!input.trim()) {
        debouncedSearch.cancel();
        setSuggestions([]);
        return;
      }
      debouncedSearch(input);
    },
    [debouncedSearch]
  );

  const selectSuggestion = useCallback(
    async (suggestionId: string): Promise<GbgRetrievedAddress | null> => {
      try {
        setIsLoading(true);
        const { data } = await axios.get<GbgAddressRetrieveResponse>(`${baseRoute}/retrieve`, {
          params: { address_id: suggestionId },
        });
        const address = { ...data.address, gbg_address_id: suggestionId };
        setRetrievedAddress(address);
        setSuggestions([]);
        return address;
      } catch (error) {
        reportAxiosError(error);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [baseRoute]
  );

  const clearSuggestions = useCallback(() => {
    debouncedSearch.cancel();
    cancelRef.current?.();
    setSuggestions([]);
    setIsLoading(false);
  }, [debouncedSearch]);

  return {
    suggestions,
    isLoading,
    retrievedAddress,
    search,
    selectSuggestion,
    clearSuggestions,
  };
};
